import { memo, useState, useCallback, useMemo, useEffect } from 'react';
import ContentUploadProgress from './ContentUploadProgress';
import useContentUpload from '@/hooks/useContentUpload';
import useWorkspace from '@/hooks/useWorkspace';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { CONTENT_FORMATS } from '@/Pages/Sessions/NewContentModal/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const ContentUpload = ({
  onUploadSuccess,
  onUploadStart,
  onUploadCancel
}: {
  onUploadSuccess?: (videoAssetId: string) => void;
  onUploadStart?: () => void;
  onUploadCancel?: () => void;
}) => {
  const { logger, adminAppStore } = useAppContext();

  const [currentContentUploadId, setCurrentContentUploadId] = useState<string>();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');

  const { workspaces, isLoading: isLoadingWorkspaces, error: workspaceError, getWorkspaceOptions } = useWorkspace();
  const isWorkspaceEnabled = Boolean(adminAppStore?.currentOrg?.is_workspace_enabled);
  const isWorkspaceFeatureFlagEnabled = featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_workspace_filters];

  const { uploadContent, onFilesUpload, cancelUpload, getContentUploadById } = useContentUpload(
    onUploadSuccess,
    selectedWorkspaceId
  );

  const { totalPercentageUploaded, file: uploadedFile } = getContentUploadById(currentContentUploadId);

  const handleWorkspaceChange = useCallback(
    (workspaceName: string) => {
      const workspace = workspaces.find(w => w.name === workspaceName);
      if (workspace) {
        setSelectedWorkspaceId(workspace.id);
      }
    },
    [workspaces]
  );

  const isWorkspaceRequired = useMemo(
    () => isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled && !selectedWorkspaceId,
    [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, selectedWorkspaceId]
  );

  const showWorkspaceDropdown = useMemo(
    () => isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled && !isLoadingWorkspaces,
    [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, isLoadingWorkspaces]
  );

  useEffect(() => {
    if (isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled && workspaces.length === 1 && !selectedWorkspaceId) {
      setSelectedWorkspaceId(workspaces[0].id);
    }
  }, [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, selectedWorkspaceId, workspaces]);

  useEffect(() => {
    if (!isWorkspaceEnabled && isWorkspaceFeatureFlagEnabled && workspaces.length > 0 && !selectedWorkspaceId) {
      const defaultWorkspace = workspaces.find(workspace => workspace.is_default === true);
      if (defaultWorkspace) {
        setSelectedWorkspaceId(defaultWorkspace.id);
      } else {
        setSelectedWorkspaceId(workspaces[0].id);
      }
    }
  }, [isWorkspaceEnabled, isWorkspaceFeatureFlagEnabled, selectedWorkspaceId, workspaces]);

  const handleFilesUpload = useCallback(
    async (files: File[]) => {
      try {
        onUploadStart?.();
        const upload = await onFilesUpload(files);
        if (upload) {
          setCurrentContentUploadId(upload.contentId);
          const content = await uploadContent(upload);
          if (content) {
            onUploadSuccess?.(content?.video_library_id ?? '');
          }
        }
      } catch (error) {
        logger.error(error);
      }
    },
    [onFilesUpload, uploadContent, onUploadSuccess, onUploadStart, logger]
  );

  const handleCancelUpload = useCallback(() => {
    if (currentContentUploadId) {
      const isCancelled = cancelUpload(currentContentUploadId);
      if (isCancelled) {
        setCurrentContentUploadId(undefined);
        onUploadCancel?.();
      }
    }
  }, [cancelUpload, currentContentUploadId, onUploadCancel]);

  if (currentContentUploadId && uploadedFile) {
    return (
      <ContentUploadProgress
        totalPercentageUploaded={totalPercentageUploaded ?? 0}
        file={uploadedFile}
        onCancel={handleCancelUpload}
      />
    );
  }
  return (
    <div className="flex h-[23.5rem] flex-col">
      {/* Workspace Selection - Only show when workspace addon is enabled */}
      {showWorkspaceDropdown && (
        <div className="mb-4">
          <label className="mb-2 block text-sm text-slate-700">Team Workspace</label>
          <SelectDropdown
            options={getWorkspaceOptions()}
            selected={workspaces.find(w => w.id === selectedWorkspaceId)?.name || ''}
            onChange={handleWorkspaceChange}
            placeholder={isLoadingWorkspaces ? 'Loading workspaces...' : 'Select an option'}
            disabled={isLoadingWorkspaces || !!currentContentUploadId}
            className="m-0 w-full cursor-pointer rounded-md border-none text-sm outline-none hover:!border-theme-input-focus focus:!border-theme-input-focus"
            buttonClassName="!h-10 focus:!ring-1 !font-normal"
          />
          {workspaceError && <p className="mt-1 text-xs text-red-600">{workspaceError}</p>}
        </div>
      )}

      <div className="grow transition-all duration-150 ease-in-out">
        <FilesInput
          onFilesUpload={handleFilesUpload}
          accept={CONTENT_FORMATS}
          title="Upload a file"
          multiple={false}
          isDisabled={isWorkspaceRequired || !!currentContentUploadId}
          disabledErrorMessage="Please select a workspace to continue uploading your file"
        />
      </div>
    </div>
  );
};

export default memo(ContentUpload);
